import React from "react";
import { Link } from "react-router-dom";
import { useAdminOverviewData } from "../../hooks/useAdminOverviewData";
import {
  ADMIN_TASK_STATUS_OPTIONS,
  adminStatusBadgeClass,
  adminStatusLabel,
} from "./adminStatusUi";
import serviceProjectIcon from "../../assets/icons/service-project.svg";
import adminContactIcon from "../../assets/icons/admin-overview-contact.svg";
import serviceAiIcon from "../../assets/icons/service-ai.svg";

function pipelineTileClass(value) {
  return `admin-dash__pipeline-tile admin-dash__pipeline-tile--${value.replace(/_/g, "-")}`;
}

function aiStatTileClass(key) {
  return `admin-dash__ai-stat-tile admin-dash__ai-stat-tile--${key}`;
}

function formatActivityRelativeTime(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return new Date(iso).toLocaleString();
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function activityRequesterLabel(raw) {
  if (!raw || typeof raw !== "string") return null;
  const x = raw.trim().toLowerCase();
  if (x === "company") return "Company";
  if (x === "individual" || x === "person") return "Individual";
  return raw.replace(/_/g, " ");
}

function activityPreviewLine(text, max = 80) {
  if (!text || typeof text !== "string") return "";
  const s = text.replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function IconActivityHub() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v5h5" />
    </svg>
  );
}

function IconActivityChevron() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
    </svg>
  );
}

function AdminOverviewSkeleton() {
  return (
    <div
      className="admin-dash__overview-skeleton"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading dashboard"
    >
      <div className="admin-dash__sk admin-dash__overview-skeleton-banner admin-dash__sk--pulse" />
      <div className="admin-dash__overview-skeleton-kpis">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="admin-dash__sk admin-dash__overview-skeleton-kpi admin-dash__sk--pulse" />
        ))}
      </div>
      <div className="admin-dash__overview-skeleton-panels">
        <div className="admin-dash__sk admin-dash__overview-skeleton-panel admin-dash__sk--pulse" />
        <div className="admin-dash__sk admin-dash__overview-skeleton-panel admin-dash__sk--pulse" />
      </div>
      <div className="admin-dash__sk admin-dash__overview-skeleton-ai admin-dash__sk--pulse" />
      <div className="admin-dash__sk admin-dash__overview-skeleton-activity admin-dash__sk--pulse" />
    </div>
  );
}

export default function AdminOverview() {
  const vm = useAdminOverviewData();

  const {
    apiDisabled,
    loading,
    failed,
    unread,
    activity,
    reqStatus,
    conStatus,
    newsletterYes,
    repliesTotal,
    aiStats,
    aiAttempts,
    unreadRequests,
    unreadContacts,
    requestsCount,
    contactsCount,
    nReq,
    aiSuccessRate,
    aiFailRate,
    emailAnalysis,
  } = vm;

  if (apiDisabled) {
    return (
      <div className="admin-dash__page admin-dash__page--overview">
        <header className="admin-dash__page-head">
          <div className="admin-dash__overview-header-text">
            <p className="admin-dash__page-eyebrow">Admin</p>
            <h1 className="admin-dash__overview-title">Dashboard</h1>
            <p className="admin-dash__overview-lede">
              Live inbox metrics from your backend appear here once the admin API is enabled for this build.
            </p>
          </div>
        </header>
        <section className="admin-dash__panel admin-dash__panel--soft">
          <h2 className="admin-dash__h2">Admin API is off</h2>
          <p className="admin-dash__panel-sub" style={{ maxWidth: 560 }}>
            For production, set <code className="admin-dash__code-inline">REACT_APP_ADMIN_API_ENABLED=true</code> in
            your host environment, run the backend, and sign in. Then reload this page.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-dash__page admin-dash__page--overview">
      <header className="admin-dash__page-head">
        <div className="admin-dash__overview-header-text">
          <p className="admin-dash__page-eyebrow">Admin</p>
          <h1 className="admin-dash__overview-title">Dashboard</h1>
          <p className="admin-dash__overview-lede">
            Inbox at a glance — pipeline stages, volumes, Gemini summaries, and recent activity.
          </p>
        </div>
      </header>

      {failed ? (
        <div className="admin-dash__error-banner" role="alert">
          Dashboard metrics could not be loaded. Check the API or try refreshing the page.
        </div>
      ) : null}

      {loading ? (
        <>
          <div className="admin-dash__load-banner" role="status">
            <span className="admin-dash__load-banner-dot" aria-hidden />
            Loading metrics from the API…
          </div>
          <AdminOverviewSkeleton />
        </>
      ) : null}

      {loading ? null : (
        <div className="admin-dash__overview-content">
          {unread > 0 ? (
            <div className="admin-dash__inbox-alert admin-dash__inbox-alert--featured" role="status">
              <div className="admin-dash__inbox-alert-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="admin-dash__inbox-alert-body">
                <strong className="admin-dash__inbox-alert-count">{unread}</strong>
                <span className="admin-dash__inbox-alert-copy">
                  unread item{unread === 1 ? "" : "s"} — open a row to mark it seen.
                </span>
              </div>
              <div className="admin-dash__inbox-alert-actions">
                <Link className="admin-dash__btn-primary admin-dash__btn-primary--compact" to="/admin/dashboard/requests">
                  Requests
                </Link>
                <Link className="admin-dash__btn-secondary admin-dash__btn-secondary--compact" to="/admin/dashboard/contact">
                  Contact
                </Link>
              </div>
            </div>
          ) : (
            <div className="admin-dash__overview-caught-up">
              <span className="admin-dash__overview-caught-up-mark" />
              Inbox clear — no unread items.
            </div>
          )}

          <div className="admin-dash__overview-kpis">
            <div
              className={`admin-dash__kpi-card${unread > 0 ? " admin-dash__kpi-card--pulse" : ""}`}
              title="Unread across requests and contact"
            >
              <span className="admin-dash__kpi-value">{unread}</span>
              <span className="admin-dash__kpi-label">Unread</span>
              <span className="admin-dash__kpi-hint">
                {unreadRequests} req · {unreadContacts} contact
              </span>
            </div>
            <Link className="admin-dash__kpi-card admin-dash__kpi-card--link" to="/admin/dashboard/requests">
              <span className="admin-dash__kpi-value admin-dash__kpi-value--teal">{requestsCount}</span>
              <span className="admin-dash__kpi-label">Service requests</span>
              <span className="admin-dash__kpi-cta">Open list →</span>
            </Link>
            <Link className="admin-dash__kpi-card admin-dash__kpi-card--link" to="/admin/dashboard/contact">
              <span className="admin-dash__kpi-value admin-dash__kpi-value--teal">{contactsCount}</span>
              <span className="admin-dash__kpi-label">Contact messages</span>
              <span className="admin-dash__kpi-cta">Open list →</span>
            </Link>
            <div className="admin-dash__kpi-card" title="Internal replies posted">
              <span className="admin-dash__kpi-value">{repliesTotal}</span>
              <span className="admin-dash__kpi-label">Team replies</span>
              <span className="admin-dash__kpi-hint">Across inbox</span>
            </div>
          </div>

          <div className="admin-dash__overview-pipeline-grid">
            <section className="admin-dash__panel admin-dash__panel--soft admin-dash__overview-panel-slot">
              <div className="admin-dash__panel-head admin-dash__overview-panel-head">
                <div className="admin-dash__overview-head-with-icon">
                  <img
                    src={serviceProjectIcon}
                    alt=""
                    width={44}
                    height={44}
                    className="admin-dash__overview-svc-icon-img"
                  />
                  <div>
                    <h2 className="admin-dash__h2">Service requests</h2>
                    <p className="admin-dash__overview-card-desc">Client pipeline counts</p>
                  </div>
                </div>
                <Link className="admin-dash__btn-text" to="/admin/dashboard/requests">
                  View all
                </Link>
              </div>
              <div className="admin-dash__overview-panel-highlight">
                <span className="admin-dash__overview-panel-highlight-k">Unread</span>
                <span className="admin-dash__overview-panel-highlight-v">{unreadRequests}</span>
              </div>
              <div className="admin-dash__pipeline-grid">
                {ADMIN_TASK_STATUS_OPTIONS.map((opt) => (
                  <div key={opt.value} className={pipelineTileClass(opt.value)}>
                    <span className="admin-dash__pipeline-tile-n">{reqStatus[opt.value]}</span>
                    <span className="admin-dash__pipeline-tile-l">{adminStatusLabel(opt.value)}</span>
                  </div>
                ))}
              </div>
              <div className="admin-dash__overview-panel-foot">
                <span>Total in inbox</span>
                <Link className="admin-dash__overview-panel-foot-a" to="/admin/dashboard/requests">
                  {requestsCount}
                </Link>
              </div>
            </section>

            <section className="admin-dash__panel admin-dash__panel--soft admin-dash__overview-panel-slot">
              <div className="admin-dash__panel-head admin-dash__overview-panel-head">
                <div className="admin-dash__overview-head-with-icon">
                  <img
                    src={adminContactIcon}
                    alt=""
                    width={44}
                    height={44}
                    className="admin-dash__overview-svc-icon-img"
                  />
                  <div>
                    <h2 className="admin-dash__h2">Contact messages</h2>
                    <p className="admin-dash__overview-card-desc">Same stages as requests</p>
                  </div>
                </div>
                <Link className="admin-dash__btn-text" to="/admin/dashboard/contact">
                  View all
                </Link>
              </div>
              <div className="admin-dash__overview-panel-highlight">
                <span className="admin-dash__overview-panel-highlight-k">Unread</span>
                <span className="admin-dash__overview-panel-highlight-v">{unreadContacts}</span>
              </div>
              <div className="admin-dash__pipeline-grid">
                {ADMIN_TASK_STATUS_OPTIONS.map((opt) => (
                  <div key={opt.value} className={pipelineTileClass(opt.value)}>
                    <span className="admin-dash__pipeline-tile-n">{conStatus[opt.value]}</span>
                    <span className="admin-dash__pipeline-tile-l">{adminStatusLabel(opt.value)}</span>
                  </div>
                ))}
              </div>
              <div className="admin-dash__overview-panel-extra">
                <span className="admin-dash__overview-panel-extra-k">Newsletter opt-in</span>
                <span className="admin-dash__overview-panel-extra-v">{newsletterYes}</span>
              </div>
              <div className="admin-dash__overview-panel-foot">
                <span>Total messages</span>
                <Link className="admin-dash__overview-panel-foot-a" to="/admin/dashboard/contact">
                  {contactsCount}
                </Link>
              </div>
            </section>
          </div>

          <section className="admin-dash__panel admin-dash__panel--soft admin-dash__overview-ai-section">
            <div className="admin-dash__panel-head admin-dash__overview-panel-head">
              <div className="admin-dash__overview-head-with-icon">
                <img
                  src={serviceAiIcon}
                  alt=""
                  width={44}
                  height={44}
                  className="admin-dash__overview-svc-icon-img"
                />
                <div>
                  <h2 className="admin-dash__h2">Gemini request analysis</h2>
                  <p className="admin-dash__overview-card-desc">
                    Google Gemini summaries per service request (worker-backed). Track ready, failed, in
                    progress, and not started.
                  </p>
                </div>
              </div>
              <Link className="admin-dash__btn-text" to="/admin/dashboard/requests">
                Browse requests
              </Link>
            </div>
            <div className="admin-dash__ai-stats-grid">
              <div className={aiStatTileClass("ok")}>
                <span className="admin-dash__ai-stat-n">{aiStats.ok}</span>
                <span className="admin-dash__ai-stat-l">Ready</span>
                <span className="admin-dash__ai-stat-hint">Summary OK</span>
              </div>
              <div className={aiStatTileClass("error")}>
                <span className="admin-dash__ai-stat-n">{aiStats.error}</span>
                <span className="admin-dash__ai-stat-l">Failed</span>
                <span className="admin-dash__ai-stat-hint">Last run error</span>
              </div>
              <div className={aiStatTileClass("pending")}>
                <span className="admin-dash__ai-stat-n">{aiStats.pending}</span>
                <span className="admin-dash__ai-stat-l">Analysing</span>
                <span className="admin-dash__ai-stat-hint">In flight</span>
              </div>
              <div className={aiStatTileClass("none")}>
                <span className="admin-dash__ai-stat-n">{aiStats.none}</span>
                <span className="admin-dash__ai-stat-l">Not started</span>
                <span className="admin-dash__ai-stat-hint">No run yet</span>
              </div>
            </div>
            <p className="admin-dash__overview-ai-meta">
              <strong>{nReq}</strong> requests total · <strong>{aiAttempts}</strong> analysis runs logged ·{" "}
              <strong>{aiSuccessRate}%</strong> with a ready summary
              {aiStats.error > 0 ? (
                <>
                  {" "}
                  · <strong className="admin-dash__overview-ai-meta-warn">{aiFailRate}%</strong> in failed state
                </>
              ) : null}
            </p>
          </section>

          <section className="admin-dash__panel admin-dash__panel--soft admin-dash__overview-ai-section">
            <div className="admin-dash__panel-head admin-dash__overview-panel-head">
              <div>
                <h2 className="admin-dash__h2">Email reply analysis</h2>
                <p className="admin-dash__overview-card-desc">
                  History of client replies and AI read status across service requests.
                </p>
              </div>
              <Link className="admin-dash__btn-text" to="/admin/dashboard/email-analysis">
                Open analyser
              </Link>
            </div>
            <div className="admin-dash__ai-stats-grid">
              <div className={aiStatTileClass("ok")}>
                <span className="admin-dash__ai-stat-n">{emailAnalysis.completed}</span>
                <span className="admin-dash__ai-stat-l">Completed</span>
              </div>
              <div className={aiStatTileClass("pending")}>
                <span className="admin-dash__ai-stat-n">{emailAnalysis.pending}</span>
                <span className="admin-dash__ai-stat-l">Pending</span>
              </div>
              <div className={aiStatTileClass("error")}>
                <span className="admin-dash__ai-stat-n">{emailAnalysis.failed}</span>
                <span className="admin-dash__ai-stat-l">Failed</span>
              </div>
              <div className={aiStatTileClass("none")}>
                <span className="admin-dash__ai-stat-n">{emailAnalysis.unreadWithClientReply}</span>
                <span className="admin-dash__ai-stat-l">Unread replies</span>
              </div>
            </div>
            <p className="admin-dash__overview-ai-meta">
              <strong>{emailAnalysis.totalInboundReplies}</strong> inbound client replies tracked by analysis.
            </p>
          </section>

          <section className="admin-dash__activity-hub" aria-labelledby="admin-activity-hub-title">
            <div className="admin-dash__activity-hub-inner">
              <header className="admin-dash__activity-hub-head">
                <div className="admin-dash__activity-hub-brand">
                  <span className="admin-dash__activity-hub-icon-wrap" aria-hidden="true">
                    <IconActivityHub />
                  </span>
                  <div className="admin-dash__activity-hub-brand-text">
                    <p className="admin-dash__activity-hub-eyebrow">Live feed</p>
                    <h2 className="admin-dash__activity-hub-title" id="admin-activity-hub-title">
                      Recent activity
                    </h2>
                    <p className="admin-dash__activity-hub-lede">
                      Newest threads across service requests and contact.{" "}
                      <strong>{repliesTotal}</strong> team {repliesTotal === 1 ? "reply" : "replies"} logged in total.
                    </p>
                  </div>
                </div>
                <div className="admin-dash__activity-hub-toolbar">
                  <span className="admin-dash__activity-hub-count-pill" title="Items returned for this feed">
                    {activity.length} in feed
                  </span>
                  <Link className="admin-dash__activity-hub-link-btn" to="/admin/dashboard/requests">
                    Requests
                  </Link>
                  <Link className="admin-dash__activity-hub-link-btn" to="/admin/dashboard/contact">
                    Contact
                  </Link>
                </div>
              </header>

              <ul className="admin-dash__activity-hub-list">
                {activity.length === 0 ? (
                  <li className="admin-dash__activity-hub-empty">
                    <span className="admin-dash__activity-hub-empty-icon" aria-hidden="true">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7Z"
                        />
                      </svg>
                    </span>
                    <p className="admin-dash__activity-hub-empty-title">Nothing here yet</p>
                    <p className="admin-dash__activity-hub-empty-desc">
                      Incoming requests and contact messages will show up with status, replies, and quick links to each
                      thread.
                    </p>
                  </li>
                ) : (
                  activity.map((it) => {
                    const preview = activityPreviewLine(it.subtitle);
                    const reqLabel = it.kind === "request" ? activityRequesterLabel(it.requesterType) : null;
                    const fullWhen = new Date(it.createdAt).toLocaleString();
                    return (
                      <li key={`${it.kind}-${it.id}`}>
                        <Link
                          className="admin-dash__activity-hub-row"
                          to={
                            it.kind === "request"
                              ? `/admin/dashboard/requests/${it.id}`
                              : `/admin/dashboard/contact/${it.id}`
                          }
                        >
                          <span
                            className={`admin-dash__activity-hub-avatar admin-dash__activity-hub-avatar--${
                              it.kind === "request" ? "req" : "con"
                            }`}
                            aria-hidden="true"
                          >
                            <img
                              src={it.kind === "request" ? serviceProjectIcon : adminContactIcon}
                              alt=""
                              width={22}
                              height={22}
                            />
                          </span>
                          <div className="admin-dash__activity-hub-main">
                            <div className="admin-dash__activity-hub-topline">
                              <span className="admin-dash__activity-hub-kind">{it.typeLabel}</span>
                              {reqLabel ? (
                                <span className="admin-dash__activity-hub-requester">{reqLabel}</span>
                              ) : null}
                            </div>
                            <span className="admin-dash__activity-hub-subject">{it.title}</span>
                            {preview ? (
                              <p className="admin-dash__activity-hub-preview" title={it.subtitle}>
                                {preview}
                              </p>
                            ) : null}
                            <div className="admin-dash__activity-hub-meta">
                              <span className={`${adminStatusBadgeClass(it.status)} admin-dash__badge-status--compact`}>
                                {adminStatusLabel(it.status)}
                              </span>
                              {it.unread ? <span className="admin-dash__badge-unread">Unread</span> : null}
                              {it.replyCount > 0 ? (
                                <span className="admin-dash__activity-hub-replies">
                                  {it.replyCount} repl{it.replyCount === 1 ? "y" : "ies"}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="admin-dash__activity-hub-tail">
                            <time
                              className="admin-dash__activity-hub-time"
                              dateTime={it.createdAt}
                              title={fullWhen}
                            >
                              <span className="admin-dash__activity-hub-time-rel">{formatActivityRelativeTime(it.createdAt)}</span>
                              <span className="admin-dash__activity-hub-time-abs">{fullWhen}</span>
                            </time>
                            <span className="admin-dash__activity-hub-chevron">
                              <IconActivityChevron />
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
